import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useLibrary } from '../context/LibraryContext';
import { useBooks } from '../context/BooksContext';
import { useUser } from '../context/UserContext';
import { useSocial } from '../context/SocialContext';
import './Badges.css';

// Badges par défaut (fallback si Firestore est vide)
const DEFAULT_BADGES = [
  { id: 'first_read', emoji: '📖', name: 'Premier Pas', description: 'Ajouter un livre à ta bibliothèque', conditionType: 'libraryCount', conditionValue: 1 },
  { id: 'bookworm', emoji: '🐛', name: 'Rat de Bibliothèque', description: 'Avoir 5 livres en bibliothèque', conditionType: 'libraryCount', conditionValue: 5 },
  { id: 'first_comment', emoji: '💬', name: 'Première Voix', description: 'Poster un commentaire', conditionType: 'hasCommented', conditionValue: 1 },
  { id: 'author', emoji: '✍️', name: 'Auteur', description: 'Publier ton premier livre', conditionType: 'booksWritten', conditionValue: 1 },
  { id: 'prolific', emoji: '📚', name: 'Prolifique', description: 'Publier 3 livres', conditionType: 'booksWritten', conditionValue: 3 },
  { id: 'popular', emoji: '🌟', name: 'Étoile Montante', description: 'Recevoir 10 likes sur tes livres', conditionType: 'totalLikesReceived', conditionValue: 10 },
  { id: 'social', emoji: '🤝', name: 'Social', description: 'Suivre 3 auteurs', conditionType: 'followingCount', conditionValue: 3 },
  { id: 'influencer', emoji: '👑', name: 'Influenceur', description: 'Avoir 5 followers', conditionType: 'followersCount', conditionValue: 5 },
  { id: 'explorer', emoji: '🧭', name: 'Explorateur', description: 'Lire des livres de 3 genres différents', conditionType: 'genresRead', conditionValue: 3 },
  { id: 'swiper', emoji: '👆', name: 'Swipeur Pro', description: 'Swiper 20 livres', conditionType: 'swipedCount', conditionValue: 20 },
];

function checkBadge(badge, stats) {
  const val = stats[badge.conditionType];
  if (val === undefined) return false;
  if (typeof val === 'boolean') return val;
  return val >= badge.conditionValue;
}

export default function Badges({ compact = false }) {
  const { library, swipedBooks } = useLibrary();
  const { books } = useBooks();
  const { user } = useUser();
  const { followers, following } = useSocial();
  const [firestoreBadges, setFirestoreBadges] = useState(null);

  // Charger les badges depuis Firestore
  useEffect(() => {
    const q = query(collection(db, 'badges'), where('active', '==', true));
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) {
        setFirestoreBadges(null); // Utiliser les defaults
      } else {
        setFirestoreBadges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    });
    return () => unsub();
  }, []);

  const badgeDefinitions = firestoreBadges || DEFAULT_BADGES;

  const stats = useMemo(() => {
    const myBooks = books.filter(b => b.authorId === user.authorId);
    const genres = new Set(library.map(b => b.genre).filter(Boolean));
    const chaptersWritten = myBooks.reduce((sum, b) => sum + (b.chapters || []).length, 0);
    const totalViews = myBooks.reduce((sum, b) => sum + (b.views || 0), 0);

    return {
      libraryCount: library.length,
      booksWritten: myBooks.length,
      totalLikesReceived: myBooks.reduce((sum, b) => sum + (b.likes || 0), 0),
      followingCount: following.length,
      followersCount: followers.length,
      genresRead: genres.size,
      swipedCount: swipedBooks.length,
      hasCommented: true,
      chaptersWritten,
      totalViews,
    };
  }, [library, books, user, followers, following, swipedBooks]);

  const earnedBadges = badgeDefinitions.filter(b => checkBadge(b, stats));
  const lockedBadges = badgeDefinitions.filter(b => !checkBadge(b, stats));

  if (compact) {
    return (
      <div className="badges-compact">
        {earnedBadges.length > 0 ? (
          earnedBadges.map(badge => (
            <motion.span
              key={badge.id}
              className="badge-emoji"
              title={badge.name}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500 }}
            >
              {badge.emoji}
            </motion.span>
          ))
        ) : (
          <span className="badges-none">Aucun badge encore</span>
        )}
      </div>
    );
  }

  return (
    <div className="badges-section">
      <h3 className="badges-title">
        Badges ({earnedBadges.length}/{badgeDefinitions.length})
      </h3>

      {earnedBadges.length > 0 && (
        <div className="badges-grid">
          {earnedBadges.map((badge, i) => (
            <motion.div
              key={badge.id}
              className="badge-card badge-earned"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
            >
              <span className="badge-card-emoji">{badge.emoji}</span>
              <h4 className="badge-card-name">{badge.name}</h4>
              <p className="badge-card-desc">{badge.description}</p>
            </motion.div>
          ))}
        </div>
      )}

      {lockedBadges.length > 0 && (
        <>
          <h4 className="badges-locked-title">À débloquer</h4>
          <div className="badges-grid">
            {lockedBadges.map((badge, i) => (
              <motion.div
                key={badge.id}
                className="badge-card badge-locked"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: (earnedBadges.length + i) * 0.05 }}
              >
                <span className="badge-card-emoji">🔒</span>
                <h4 className="badge-card-name">{badge.name}</h4>
                <p className="badge-card-desc">{badge.description}</p>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}